import { FC, useContext, useEffect } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import arcadeApiClient from '../../apiClients/arcadeApiClient';
import { AppContext, AuthContext, MPThemeContext, SpectatorViewContext } from '../../providers';
import { Routes } from '../../types';
import { useEntity } from 'hooks/useEntity';
import { useRoles } from 'hooks/useRoles';
import { useUser } from 'hooks/useUser';
import { useIsEmbedded } from 'providers/MPEmbeddedContextProvider';
import { profileDropdownLinks } from './profileDropdownLinks';
import { mainLinks } from './mainLinks';
import { ArcadeLogo } from 'ui/assets/ArcadeLogo';
import { ArcadeIcon } from 'ui/assets/ArcadeIcon';
import { RTMeIcon } from 'ui/assets/RTMeIcon';
import { RTMeLogo } from 'ui/assets/RTMeLogo';
import { CbNavigationBar } from 'ui/components/CbNavigationBar';
import { userRoleIsAtLeast } from '../../helpers/roles';
import { mobileSideNavLinks } from './mobileSideNavLinks';
import { LowBalanceBanner } from './LowBalanceBanner';
import { RequiresVerificationBanner } from './RequiresVerificationBanner';
import { MaintenanceBanner } from './MaintenanceBanner';
const fetcher = async (url: string) => await arcadeApiClient.get(url).then(res => res.data);
export const NavigationContainer: FC = () => {
  const {
    auth
  } = useContext(AuthContext);
  const {
    setTitle
  } = useContext(AppContext);
  const {
    isMobile
  } = useContext(MPThemeContext);
  const {
    entity,
    isRestricted
  } = useEntity();
  const router = useRouter();
  const {
    assumedUser,
    setAssumedUser,
    reset
  } = useContext(SpectatorViewContext);
  const {
    user,
    refetch: reloadCurrentUser,
    error: userError
  } = useUser();
  const {
    data: lootData,
    error: lootBoxesError
  } = useSWR('/multi_platform/profile/unopened_loot_boxes', fetcher);
  const role = useRoles();
  const canBuyTokens = userRoleIsAtLeast({
    requiredRole: 'champion',
    userRole: role
  });
  const isManager = userRoleIsAtLeast({
    requiredRole: 'company_manager',
    userRole: role
  });
  const {
    isEmbedded
  } = useIsEmbedded();
  const {
    data: alertData
  } = useSWR(isManager ? '/multi_platform/manage/alerts.json' : null, fetcher);
  const hasSpectatorView = isManager && !isEmbedded && (router.pathname === '/games' || router.pathname === '/games/[id]' || router.pathname === '/stats' || router.pathname === '/newsfeed/[feedType]');
  useEffect(() => {
    if (router.query?.player_id) {
      arcadeApiClient.put('/multi_platform/users/set_player_id', {
        player_id: router.query.player_id
      });
    }
  }, []);
  useEffect(() => {
    if (entity && !entity.maintenanceMode) {
      sessionStorage.removeItem('maintenance-mode');
    }
  }, [entity?.maintenanceMode]);
  const isFetchingUser = !user && !userError;
  const tokenBalance = user?.tokenBalance;
  const isFetchingChests = !lootData && !lootBoxesError;
  const raffleTicketCount = lootData?.lootBoxes.filter(lootBox => lootBox.isRaffleTicket).length;
  const lootBoxCount = lootData?.lootBoxes.length - raffleTicketCount;
  const hasChestBalance = lootBoxCount > 0;
  const {
    isTeams,
    endpoint
  } = auth;
  const {
    pathname,
    query
  } = router;
  const isRTMe = endpoint.includes('rtposapi');

  // We don't show the token alert on the manage transactions page because this is the page where the manager would
  // be topping up the tokens.
  const hasTokenAlert = pathname != '/manage/transactions' && alertData?.lowTokenAlert;
  const arcadeLogo = {
    as: NextLink,
    element: <ArcadeLogo width={isMobile ? '120px' : '98px'} height={isMobile ? '23px' : '18px'} marginBottom="3px" />,
    mobileElement: <ArcadeIcon width="32px" height="32px" />,
    href: Routes.Games
  };
  const RTLogo = {
    as: NextLink,
    element: <RTMeLogo width={isMobile ? '120px' : '98px'} height={isMobile ? '23px' : '18px'} marginBottom="3px" />,
    mobileElement: <RTMeIcon width="40px" height="40px" />,
    href: Routes.Games
  };
  const profile = !!user ? profileDropdownLinks({
    pathname: pathname,
    userData: user,
    setTitle: setTitle,
    isTeams: isTeams
  }) : undefined;
  const fullWidth = isTeams;
  const baseLogo = isRTMe ? RTLogo : arcadeLogo;
  const logo = isTeams ? undefined : baseLogo;
  const handleChestBalanceClick = async (): Promise<void> => {
    router.push({
      pathname: Routes.RewardsActivity
    }).catch(err => console.error('err', err));
  };
  const handleTokenBalanceClick = async (): Promise<void> => {
    if (isRestricted) {
      router.push(Routes.RewardsActivity).catch(err => console.error('err', err));
    } else {
      router.push(Routes.Rewards).catch(err => console.error('err', err));
    }
  };
  return <>
      <CbNavigationBar hasSpectatorView={hasSpectatorView} assumedUser={assumedUser} companyName={entity?.name} onAssumableUserChange={setAssumedUser} onResetClick={reset} chestBalance={lootBoxCount} chestBalancePending={isFetchingChests} fullWidth={fullWidth} hasChestBalance={hasChestBalance} isMobile={isMobile} links={mainLinks({
      isRestricted,
      pathname,
      role,
      query
    })} mobileLinks={mobileSideNavLinks({
      isRestricted,
      pathname,
      query
    })} logo={logo} profile={profile} raffleTicketBalance={raffleTicketCount} raffleTicketBalancePending={isFetchingChests} showMaintenanceWarning={entity?.maintenanceMode} tokenBalance={tokenBalance} tokenBalancePending={isFetchingUser} onChestBalanceClick={handleChestBalanceClick} onTokenBalanceClick={handleTokenBalanceClick} data-sentry-element="CbNavigationBar" data-sentry-source-file="NavigationContainer.tsx" />
      {entity?.maintenanceMode && <MaintenanceBanner />}
      {hasTokenAlert && canBuyTokens && <LowBalanceBanner />}
      {user?.requiresEmailVerification && <RequiresVerificationBanner email={user.email} onEmailVerified={reloadCurrentUser} />}
    </>;
};